/**
 * Chakra Dashboard page - energy visualization and check-ins
 * Premium feature for tracking chakra balance
 */
import { useEffect, useRef, useState } from 'react';
import { Lock, TrendingUp, Brain, Heart, Zap } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Button } from '@/components/ui';
import { useLanguage, useAuthStore } from '@/store';
import { useNavigate } from 'react-router-dom';
import { getChakraTranslation } from '@/utils';
import { hasAccess } from '@/utils/access';

// Chakra data with colors and positions
const CHAKRAS = [
  { key: 'crown', nameKey: 'chakraCrown' as const, color: '#9333EA', y: 0.1 },
  { key: 'third_eye', nameKey: 'chakraThirdEye' as const, color: '#6366F1', y: 0.2 },
  { key: 'throat', nameKey: 'chakraThroat' as const, color: '#0EA5E9', y: 0.32 },
  { key: 'heart', nameKey: 'chakraHeart' as const, color: '#16A34A', y: 0.45 },
  { key: 'solar_plexus', nameKey: 'chakraSolarPlexus' as const, color: '#EAB308', y: 0.58 },
  { key: 'sacral', nameKey: 'chakraSacral' as const, color: '#EA580C', y: 0.72 },
  { key: 'root', nameKey: 'chakraRoot' as const, color: '#DC2626', y: 0.88 },
];

interface ChakraBalance {
  [key: string]: number;
}

export function ChakraPage() {
  const language = useLanguage();
  const { user, isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | undefined>(undefined);
  // TODO: setChakraBalance will be used when API is connected for check-ins
const [chakraBalance] = useState<ChakraBalance>({
    crown: 75,
    third_eye: 60,
    throat: 80,
    heart: 90,
    solar_plexus: 55,
    sacral: 70,
    root: 65,
  });
  // Every plan and the trial get the same access (R2, R5).
  const isPremium = hasAccess(user);

  const t = (key: Parameters<typeof getChakraTranslation>[0]) => getChakraTranslation(key, language);

  // Animation loop for chakra visualization
  useEffect(() => {
    if (!canvasRef.current || !isPremium) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let rotation = 0;

    const draw = () => {
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      // Draw connecting line
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.moveTo(centerX, height * 0.1);
      ctx.lineTo(centerX, height * 0.88);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw each chakra
      CHAKRAS.forEach((chakra) => {
        const y = height * chakra.y;
        const balance = chakraBalance[chakra.key] || 50;
        const radius = 15 + (balance / 100) * 20;

        // Glow effect
        const gradient = ctx.createRadialGradient(centerX, y, 0, centerX, y, radius + 30);
        gradient.addColorStop(0, chakra.color + '60');
        gradient.addColorStop(0.5, chakra.color + '30');
        gradient.addColorStop(1, chakra.color + '00');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(centerX, y, radius + 30, 0, Math.PI * 2);
        ctx.fill();

        // Main circle
        ctx.fillStyle = chakra.color;
        ctx.beginPath();
        ctx.arc(centerX, y, radius, 0, Math.PI * 2);
        ctx.fill();

        // Inner circle
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.beginPath();
        ctx.arc(centerX, y, radius * 0.5, 0, Math.PI * 2);
        ctx.fill();

        // Rotating pattern
        ctx.save();
        ctx.translate(centerX, y);
        ctx.rotate(rotation);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        for (let i = 0; i < 6; i++) {
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.lineTo(radius * 0.7, 0);
          ctx.stroke();
          ctx.rotate(Math.PI / 3);
        }
        ctx.restore();
      });

      rotation += 0.01;
      animationRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [chakraBalance, isPremium]);

  if (!isAuthenticated) {
    return (
      <div className="container py-16 max-w-lg mx-auto text-center">
        <Card>
          <CardHeader>
            <div className="flex justify-center mb-4">
              <Lock className="h-16 w-16 text-muted-foreground" />
            </div>
            <CardTitle>
              {t('loginRequired')}
            </CardTitle>
            <CardDescription>
              {t('loginDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/login')}>
              {t('login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isPremium) {
    return (
      <div className="container py-16 max-w-lg mx-auto text-center">
        <Card className="gradient-border">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <div className="relative">
                <Zap className="h-16 w-16 text-primary" />
                <Lock className="h-6 w-6 text-lukaaji-gold absolute -bottom-1 -right-1" />
              </div>
            </div>
            <CardTitle className="text-gradient">
              {t('premiumFeature')}
            </CardTitle>
            <CardDescription>
              {t('premiumDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {t('premiumUse')}
            </p>
            <Button className="gradient-primary" onClick={() => navigate('/pricing')}>
              {t('upgradeToPremium')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Premium user - show chakra dashboard
  return (
    <div className="container py-8">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-2">
          {t('chakraDashboard')}
        </h1>
        <p className="text-muted-foreground">
          {t('monitorEnergyCenters')}
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Chakra Visualization */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('energyVisualization')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-center">
              <canvas
                ref={canvasRef}
                width={200}
                height={400}
                className="rounded-lg"
              />
            </div>
          </CardContent>
        </Card>

        {/* Chakra Legend */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('chakraBalance')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {CHAKRAS.map((chakra) => (
                <div key={chakra.key} className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: chakra.color }}
                  />
                  <span className="flex-1">
                    {t(chakra.nameKey)}
                  </span>
                  <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all"
                      style={{
                        width: `${chakraBalance[chakra.key]}%`,
                        backgroundColor: chakra.color,
                      }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground w-12 text-right">
                    {chakraBalance[chakra.key]}%
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trends */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle>
              {t('energyTrends')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <TrendingUp className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <div className="text-sm text-muted-foreground">
                  {t('trend')}
                </div>
                <div className="font-semibold">
                  {t('improving')}
                </div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Zap className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
                <div className="text-sm text-muted-foreground">
                  {t('avgEnergy')}
                </div>
                <div className="font-semibold">7.2/10</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Brain className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                <div className="text-sm text-muted-foreground">
                  {t('mentalClarity')}
                </div>
                <div className="font-semibold">8.0/10</div>
              </div>
              <div className="text-center p-4 bg-muted/30 rounded-lg">
                <Heart className="h-8 w-8 mx-auto mb-2 text-red-500" />
                <div className="text-sm text-muted-foreground">
                  {t('emotionalBalance')}
                </div>
                <div className="font-semibold">7.5/10</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
